import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, Palette, Leaf, ArrowRight, Sparkles } from "lucide-react";

const features = [
  { icon: Palette, label: "8 Pickle Bases", desc: "Mango, Lemon, Gongura, Garlic & more" },
  { icon: SlidersHorizontal, label: "Deep Taste Control", desc: "Spice, Salt, Oil, Sourness, Texture" },
  { icon: Leaf, label: "5 Wellness Profiles", desc: "Kid-Friendly to Heart-Healthy" },
];

export const StudioTeaser = () => {
  const navigate = useNavigate();
  return (
    <section id="customization-studio" data-testid="studio-teaser-section" className="relative overflow-hidden py-24 lg:py-36">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={`${process.env.PUBLIC_URL}/kitchen.png`}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center opacity-80"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(248,244,237,0.86)_0%,rgba(248,244,237,0.72)_36%,rgba(248,244,237,0.88)_100%)]" />
        <div className="absolute inset-0 spice-dust" />
      </div>
      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="text-sm font-medium text-earth-green uppercase tracking-wider flex items-center justify-center gap-2">
            <Sparkles size={14} />
            Customization Studio
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
            Your kitchen, <span className="font-accent italic text-earth-green">your rules</span>
          </h2>
          <p className="text-base text-sw-text-secondary mt-4 max-w-lg mx-auto">
            Build a pickle from scratch. Choose your base, dial in every flavor, select your ingredients, and create something uniquely yours.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-3 gap-5 max-w-3xl mx-auto mb-12">
          {features.map((feat, i) => (
            <motion.div
              key={feat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`studio-feature-${i}`}
              className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center"
            >
              <div className="w-10 h-10 rounded-xl bg-earth-green/10 flex items-center justify-center mx-auto mb-3">
                <feat.icon size={20} className="text-earth-green" />
              </div>
              <p className="text-sm font-heading font-600 text-sw-text">{feat.label}</p>
              <p className="text-xs text-sw-text-muted mt-1">{feat.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <button
            data-testid="studio-teaser-cta"
            onClick={() => navigate("/studio")}
            className="bg-earth-green text-white rounded-full px-10 py-4 font-medium text-base hover:bg-earth-green-dark transition-all duration-300 shadow-lg shadow-earth-green/20 hover:shadow-xl hover:shadow-earth-green/30 inline-flex items-center gap-2 group"
          >
            Launch the Studio
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
